/**
 * Service Worker Registration Module
 * Handles registration and offline state management
 */

// Service Worker state
let serviceWorker: ServiceWorker | null = null;
let isOffline = !navigator.onLine;
let connectivityListenersReady = false;
let messageListenerReady = false;

// GARDEN-191: Debug flag for development logging
const DEBUG = false;

// Callbacks for UI updates
const offlineListeners: Array<(offline: boolean) => void> = [];

/**
 * Register the service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorker | null> {
  setupConnectivityListeners();

  if (!('serviceWorker' in navigator)) {
    if (DEBUG) {
      console.log('[SW] Service workers not supported');
    }
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    });

    if (DEBUG) {
      console.log('[SW] Registered with scope:', registration.scope);
    }

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            if (DEBUG) {
              console.log('[SW] New version available');
            }
            // Could show update notification here
          }
        });
      }
    });

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    
    // Get the active service worker
    if (registration.active) {
      serviceWorker = registration.active;
    }

    // Listen for messages from service worker
    if (!messageListenerReady) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
      messageListenerReady = true;
    }

    return serviceWorker;
  } catch (error) {
    if (DEBUG) {
      console.error('[SW] Registration failed:', error);
    }
    return null;
  }
}

/**
 * Unregister the service worker (for debugging)
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  if (registration) {
    const result = await registration.unregister();
    if (DEBUG) {
      console.log('[SW] Unregistered:', result);
    }
    return result;
  }
  return false;
}

/**
 * Update the service worker
 */
export async function updateServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  if (registration) {
    await registration.update();
    if (DEBUG) {
      console.log('[SW] Update check completed');
    }
  }
}

/**
 * Clear all caches
 */
export async function clearCaches(): Promise<boolean> {
  if (!serviceWorker) {
    return false;
  }

  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      resolve(event.data?.success || false);
    };
    serviceWorker?.postMessage({ type: 'CLEAR_CACHE' }, [channel.port2]);
  });
}

/**
 * Check if currently offline
 */
export function getIsOffline(): boolean {
  return isOffline;
}

/**
 * Subscribe to offline state changes
 */
export function onOfflineChange(callback: (offline: boolean) => void): () => void {
  setupConnectivityListeners();
  offlineListeners.push(callback);
  callback(isOffline);
  
  // Return unsubscribe function
  return () => {
    const index = offlineListeners.indexOf(callback);
    if (index > -1) {
      offlineListeners.splice(index, 1);
    }
  };
}

/**
 * Set up online/offline event listeners
 */
function setupConnectivityListeners(): void {
  if (connectivityListenersReady) return;
  connectivityListenersReady = true;

  window.addEventListener('online', () => {
    if (DEBUG) {
      console.log('[SW] Connection restored');
    }
    isOffline = false;
    notifyOfflineChange(false);
  });

  window.addEventListener('offline', () => {
    if (DEBUG) {
      console.log('[SW] Connection lost');
    }
    isOffline = true;
    notifyOfflineChange(true);
  });

  // Also listen for connection quality changes (if supported)
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection) {
      connection.addEventListener('change', () => {
        if (DEBUG) {
          console.log('[SW] Connection changed:', connection.effectiveType);
        }
      });
    }
  }
}

/**
 * Notify all listeners of offline state change
 */
function notifyOfflineChange(offline: boolean): void {
  offlineListeners.forEach(callback => {
    try {
      callback(offline);
    } catch (error) {
      if (DEBUG) {
        console.error('[SW] Error in offline listener:', error);
      }
    }
  });
}

/**
 * Handle messages from service worker
 */
function handleSWMessage(event: MessageEvent): void {
  const { data } = event;
  
  if (data?.type === 'CACHE_UPDATED') {
    if (DEBUG) {
      console.log('[SW] Cache updated:', data.url);
    }
  }
  
}

/**
 * Force skip waiting for new service worker
 */
export function skipWaiting(): void {
  if (serviceWorker) {
    serviceWorker.postMessage('skipWaiting');
  }
}

/**
 * Check if app can work offline
 */
export async function canWorkOffline(): Promise<boolean> {
  if (!('caches' in window)) {
    return false;
  }

  try {
    const cacheNames = await caches.keys();
    return cacheNames.length > 0;
  } catch {
    return false;
  }
}
